/* WEEK EDITOR STAGE 2 — SAVE THIS WEEK AS A PROGRAM (2026-10-19, `docs/WEEK-EDITOR-STAGE2-PLAN.md` §4).
 *
 * A program names LIBRARY TEMPLATES by week and weekday (`coach_program_days`, migration 20260920130000) — never a client's
 * copies. So saving a week is: each workout copied into the library (the database's `clone_coach_workout`, never a TypeScript
 * copy), the program head, its day rows — ALL in ONE transaction (`save_week_as_program`, SECURITY INVOKER). This module is the
 * page's half: which days, under which template names (the existing `libraryName` rule, unique against the library AND each
 * other), and what the function's refusals say. `rpc` is injected so `weekProgram.test.ts` drives every branch offline. */
import { libraryName } from "./builder";
import { fitWorkoutName } from "./workoutNameCap";

const WEEKDAY = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export const SAVE_WEEK_AS_PROGRAM_RPC = "save_week_as_program" as const;

export type ProgramDay = { workout_id: string; weekday: number; position: number; template_name: string };

export type ProgramRpc = (fn: typeof SAVE_WEEK_AS_PROGRAM_RPC, args: { p_name: string; p_days: ProgramDay[] }) =>
  PromiseLike<{ data: unknown; error: { message: string } | null }>;

/** ISO weekday of a day key: Monday 1 … Sunday 7 (the program's own numbering, `coach_program_days.weekday`). */
export function isoWeekday(dayKey: string): number {
  const d = new Date(`${dayKey}T00:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

/** The week's workouts as program days, in order, each with the name its template will carry in the library: the workout's
 *  name and its WEEKDAY (`Upper · Tue`) — the first render named them `Upper (copy)`, `Upper (copy 2)`, `Lower (copy 3)`, which
 *  told a coach nothing (MEASURED in the example, 2026-10-19). Within the 28-character cap; `libraryName` keeps it unique
 *  against the library and the week's others. */
export function programDaysOf(days: readonly { dayKey: string; workouts: readonly { workoutId: string; name: string }[] }[],
                              libraryNames: readonly string[]): ProgramDay[] {
  const taken = [...libraryNames];
  const out: ProgramDay[] = [];
  for (const d of days) {
    d.workouts.forEach((w, position) => {
      const template_name = libraryName(taken, fitWorkoutName(w.name.trim(), ` · ${WEEKDAY[isoWeekday(d.dayKey) - 1]!}`));
      taken.push(template_name);
      out.push({ workout_id: w.workoutId, weekday: isoWeekday(d.dayKey), position, template_name });
    });
  }
  return out;
}

/** The program's default name: the client's first name and the week's Monday (`Alex · week of 19 Oct`). */
export function defaultProgramName(clientName: string | undefined, mondayLabel: string): string {
  const first = (clientName ?? "").trim().split(/\s+/)[0] ?? "";
  return first === "" ? `Week of ${mondayLabel}` : `${first} · week of ${mondayLabel}`;
}

const SAID: Readonly<Record<string, string>> = {
  AUTH: "You are signed out — sign in again to save this week.",
  NAME: "Give the program a name.",
  EMPTY: "This week has no workouts to save.",
  TEMPLATE: "Only a client's own workouts can be saved this way.",
  CLIENTS: "A program is saved from one client's week.",
  DAYS: "Those days do not make one week.",
};

export async function saveWeekAsProgram(rpc: ProgramRpc, name: string, days: ProgramDay[]):
  Promise<{ ok: true; programId: string } | { ok: false; said: string }> {
  if (name.trim() === "") return { ok: false, said: SAID["NAME"]! };
  if (days.length === 0) return { ok: false, said: SAID["EMPTY"]! };
  const r = await rpc(SAVE_WEEK_AS_PROGRAM_RPC, { p_name: name.trim(), p_days: days });
  if (r.error !== null) {
    const word = /PROGRAM-([A-Z]+)/.exec(r.error.message)?.[1] ?? "";
    return { ok: false, said: SAID[word] ?? `Not saved: ${r.error.message}` };
  }
  return typeof r.data === "string" ? { ok: true, programId: r.data } : { ok: false, said: "Not saved: the program came back without an id." };
}
